/**
 * UserPrivate Services
 *
 * Handles secure operations on the UserPrivate collection which contains
 * sensitive user data like email addresses and phone numbers.
 *
 * Security: Only the user themselves or admins can access this data.
 */

import { db } from "../config";
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from "firebase/firestore";

const USER_PRIVATE_COLLECTION = "UserPrivate";

/**
 * Get user's private data (email, phone, preferences)
 * @param {string} userId - The user ID
 * @returns {Promise<Object|null>} Private data or null if not found
 */
export const getUserPrivateData = async (userId) => {
  try {
    const privateDocRef = doc(db, USER_PRIVATE_COLLECTION, userId);
    const privateDoc = await getDoc(privateDocRef);

    if (!privateDoc.exists()) {
      console.warn(`UserPrivate document not found for user ${userId}`);

      // Fallback: Check Users collection for backward compatibility during migration
      // This will be removed after Phase 3 cleanup
      const userDocRef = doc(db, "Users", userId);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists() && userDoc.data().email) {
        console.log(`Falling back to Users collection for ${userId} (migration pending)`);
        return {
          email: userDoc.data().email,
          phoneNumber: userDoc.data().phoneNumber || null,
          notificationPreferences: userDoc.data().notificationPreferences || {
            email: true,
            push: true,
            newFollower: true,
            clubActivity: true,
          },
        };
      }

      return null;
    }

    return {
      id: privateDoc.id,
      ...privateDoc.data(),
    };
  } catch (error) {
    console.error("Error getting user private data:", error);
    throw error;
  }
};

/**
 * Create user private data document
 * Should only be called during user signup
 * @param {string} userId - The user ID
 * @param {Object} privateData - Private data to store
 * @returns {Promise<Object>} Success result
 */
export const createUserPrivateData = async (userId, privateData) => {
  try {
    const privateDocRef = doc(db, USER_PRIVATE_COLLECTION, userId);

    const data = {
      email: privateData.email,
      phoneNumber: privateData.phoneNumber || null,
      notificationPreferences: privateData.notificationPreferences || {
        email: true,
        push: true,
        newFollower: true,
        clubActivity: true,
      },
      createdAt: serverTimestamp(),
    };

    await setDoc(privateDocRef, data);

    return { success: true };
  } catch (error) {
    console.error("Error creating user private data:", error);
    throw error;
  }
};

/**
 * Update user's private data
 * @param {string} userId - The user ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Success result
 */
export const updateUserPrivateData = async (userId, updates) => {
  try {
    const privateDocRef = doc(db, USER_PRIVATE_COLLECTION, userId);
    const privateDoc = await getDoc(privateDocRef);

    if (!privateDoc.exists()) {
      throw new Error("UserPrivate document not found. User may need to be migrated.");
    }

    await updateDoc(privateDocRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });

    return { success: true };
  } catch (error) {
    console.error("Error updating user private data:", error);
    throw error;
  }
};

/**
 * Update notification preferences
 * @param {string} userId - The user ID
 * @param {Object} preferences - Notification preferences
 * @returns {Promise<Object>} Success result
 */
export const updateNotificationPreferences = async (userId, preferences) => {
  try {
    return await updateUserPrivateData(userId, {
      notificationPreferences: preferences,
    });
  } catch (error) {
    console.error("Error updating notification preferences:", error);
    throw error;
  }
};

/**
 * Get user's email address
 * Convenience method for when you just need the email
 * @param {string} userId - The user ID
 * @returns {Promise<string|null>} Email address or null
 */
export const getUserEmail = async (userId) => {
  try {
    const privateData = await getUserPrivateData(userId);
    return privateData?.email || null;
  } catch (error) {
    console.error("Error getting user email:", error);
    throw error;
  }
};

/**
 * Check if user has email notifications enabled
 * @param {string} userId - The user ID
 * @returns {Promise<boolean>} True if email notifications enabled
 */
export const isEmailNotificationEnabled = async (userId) => {
  try {
    const privateData = await getUserPrivateData(userId);
    return privateData?.notificationPreferences?.email ?? true;
  } catch (error) {
    console.error("Error checking email notification status:", error);
    return false; // Default to false on error
  }
};
